"use client";

import Link from "next/link";
import React from "react";
import { CONFIGS } from "@/constants/configs";
import { Logo } from "@/components/Logo";
import { useTranslations } from "@/providers/TranslationsProvider";

const LINKS = ["privacy", "terms", "contact", "doctors", "join"] as const;
const FALLBACK: Record<(typeof LINKS)[number], string> = {
  privacy: "Privacy",
  terms: "Terms",
  contact: "Contact",
  doctors: "Doctors",
  join: "Join as a doctor",
};

const Footer: React.FC<{ 
  lang?: string;
}> = ({
  lang = "en",
}) => {
  const { t } = useTranslations();
  const displayName = CONFIGS.app.displayName[lang as keyof typeof CONFIGS.app.displayName] || CONFIGS.app.displayName.en;

  return (
    <footer className="w-full bg-[var(--footer-bg)] border-t border-[var(--border-subtle)] py-12 mt-auto">
      <div className="max-w-7xl mx-auto px-6 flex flex-col items-center text-center">
        <div className="flex items-center gap-1 mb-8">
          <Logo size={32} className="drop-shadow-sm" />
          <p className="text-[#1D1D1F] dark:text-[#F5F5F7] font-bold text-base tracking-tight">
            {displayName}
          </p>
        </div>
        
        <div className="w-full h-px bg-black/[0.05] dark:bg-white/[0.1] max-w-xs mb-8"></div>
        
        <nav aria-label={t("footer.links", "Footer")} className="mb-8">
          <ul className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            {LINKS.map((link) => (
              <li key={link}>
                <Link
                  href={link === "doctors" ? `/${lang}#doctors` : `/${lang}/${link}`}
                  className="text-[12px] font-bold text-[#6E6E73] dark:text-[#86868B] hover:text-[#1D1D1F] dark:hover:text-[#F5F5F7] transition-colors duration-200"
                >
                  {t(`footer.${link}`, FALLBACK[link])}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <p className="text-[10px] font-semibold text-[#6E6E73]/60 dark:text-[#86868B]/60 uppercase tracking-[0.2em]">
          &copy; {new Date().getFullYear()} {displayName}. {t("footer.all rights reserved", "All rights reserved.")}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
